import React, { useCallback, useState } from 'react';

import { ModalView } from '@/design-system/components/modal/ModalView';
import { useModalView } from '@/design-system/components/modal/useModalView.hook';
import { getObjectScreenNameFromPath, popStateLockedMap } from '@/design-system/components/navigatorEmbed/UtxAppNavigatorEmbed';
import { useFocusEffect } from '@react-navigation/native';
import * as DocumentPicker from 'expo-document-picker';
import { Platform } from 'react-native';
import BillPaymentApp from '../..';
import { OtpConfirmType } from '../../enum';
import { PAYMENT_TYPE, QuoteDataRequest, QuoteResponse } from '../../hooks/useCreateBillPayment.hook';
import { BillPaymentStackRoutes } from '../../payment.navigator';
import { mapUrlLink } from '../../utils/utils';
import { GoogleConfirmationWeb } from './GoogleOtpConfirmation.desktop.component';
import { SmsConfirmationWeb } from './SmsOtpConfirmation.desktop.component';
import { OtpConfirmationStackRoutes, OtpConfirmationStackScreenProps } from './type';

export interface OtpConfirmModalProps {
  request: QuoteDataRequest;
  response: QuoteResponse;
  onResult: () => void;
  debitAccount: string;
  flagUploaded?: boolean;
  mode?: OtpConfirmType.SMS_OTP | OtpConfirmType.GOOGLE_OTP;
  paymentType: PAYMENT_TYPE;
  documentSelected?: DocumentPicker.DocumentResult;
  userFee: string;
}

export const OtpConfirmationModal: React.FC<OtpConfirmationStackScreenProps<OtpConfirmationStackRoutes.OtpConfirmation>> = ({
  navigation,
  route
}) => {
  const { request, response, debitAccount, flagUploaded, mode, paymentType, userFee, documentSelected } = route.params || {};
  const [switchPage, setSwitchPage] = useState<string>(mode ?? OtpConfirmType.SMS_OTP);
  const { useModalProps } = useModalView({});

  const onGoBack = () => {
    if (Platform.OS === 'web') {
      popStateLockedMap.set('popstate', true);
      navigation.goBack();
      setTimeout(() => popStateLockedMap.set('popstate', false), 500);
    } else {
      navigation.goBack();
    }
  };

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'web') {
        const onPopState = (event: any) => {
          window.history.pushState(event.state, '');
          setTimeout(() => {
            const popStateLocked = popStateLockedMap.get('popstate') ?? false;
            if (!document.location.href || popStateLocked) return;
            const { parentName, subName } = mapUrlLink(document.location.href);
            var screenNameObject = getObjectScreenNameFromPath([BillPaymentApp], `/${subName}`);
            if (
              screenNameObject.screenName &&
              screenNameObject.routeStackName === parentName &&
              (screenNameObject.screenName === BillPaymentStackRoutes.OtpConfirmation ||
                screenNameObject.screenName === BillPaymentStackRoutes.Confirmation)
            ) {
              onGoBack();
            }
          }, 200);
        };
        window.addEventListener('popstate', onPopState);
        return () => {
          window.removeEventListener('popstate', onPopState);
        };
      }
    }, [])
  );

  return (
    <ModalView useModalProps={useModalProps} minWidth={400}>
      {switchPage === OtpConfirmType.GOOGLE_OTP ? (
        <GoogleConfirmationWeb
          onClose={onGoBack}
          request={request}
          flagUploaded={flagUploaded}
          response={response}
          debitAccount={debitAccount}
          paymentType={paymentType}
          userFee={userFee}
          documentSelected={documentSelected}
        />
      ) : (
        <SmsConfirmationWeb
          onClose={onGoBack}
          request={request}
          flagUploaded={flagUploaded}
          response={response}
          debitAccount={debitAccount}
          paymentType={paymentType}
          userFee={userFee}
          documentSelected={documentSelected}
        />
      )}
    </ModalView>
  );
};
